import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LinkIcon, LockSimpleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import type { EditingTeamRole } from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import type {
  CourseModernizerReviewTeamManagement,
  CourseEvaluatorReviewDocumentIssueKind,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarItem,
} from "../types/reviewTypes";
import GapAnalysis from "./GapAnalysis";
import TeamManagementPanel, { type TeamManagementUserOption } from "./TeamManagementPanel";
import { colors } from "../../../../../styles/design-system";

type OverviewTabKey = "overview" | "gapAnalysis" | "team";

interface OverviewPanelProps {
  review: CourseEvaluatorReviewData;
  isArchitect: boolean;
  currentUserId: string | null;
  teamManagement: CourseModernizerReviewTeamManagement | null;
  teamUserOptions: TeamManagementUserOption[];
  teamSearchQuery: string;
  onTeamSearchQueryChange: (value: string) => void;
  selectedTeamRole: EditingTeamRole;
  onSelectedTeamRoleChange: (role: EditingTeamRole) => void;
  isSavingTeamManagement: boolean;
  onEditingTeamChange: (nextMembers: Array<{ userId: string; role: EditingTeamRole }>) => void;
  onStartReviewing: () => void;
  onOverlayVisibilityChange?: (isVisible: boolean) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onRejectWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  canEdit?: boolean;
}

function renderDocumentIssueIcon(documentIssueKind: CourseEvaluatorReviewDocumentIssueKind) {
  if (documentIssueKind === "permissionDenied") {
    return <LockSimpleIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
  }

  return <LinkIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
}

function OverviewPanel({
  review,
  isArchitect,
  currentUserId,
  teamManagement,
  teamUserOptions,
  teamSearchQuery,
  onTeamSearchQueryChange,
  selectedTeamRole,
  onSelectedTeamRoleChange,
  isSavingTeamManagement,
  onEditingTeamChange,
  onStartReviewing,
  onOverlayVisibilityChange,
  onAccept,
  onReject,
  onAcceptWithoutNav,
  onRejectWithoutNav,
  onJumpToEntity,
  canEdit = true,
}: OverviewPanelProps) {
  const [activeTab, setActiveTab] = useState<OverviewTabKey>("overview");

  useEffect(() => {
    if (!isArchitect && activeTab === "team") {
      setActiveTab("overview");
    }
  }, [activeTab, isArchitect]);

  const renderGapAnalysis = () => (
    <GapAnalysis
      review={review}
      onStartReviewing={onStartReviewing}
      onOverlayVisibilityChange={onOverlayVisibilityChange}
      onAccept={onAccept}
      onReject={onReject}
      onAcceptWithoutNav={onAcceptWithoutNav}
      onRejectWithoutNav={onRejectWithoutNav}
      onJumpToEntity={onJumpToEntity}
      canEdit={canEdit}
    />
  );

  const renderDocumentIssues = () =>
    review.documentIssues.length > 0 && (
      <View style={styles.card}>
        <View style={styles.documentHeader}>
          <WarningCircleIcon size={20} color="#DC6803" weight="fill" />
          <CustomText
            text={`${review.documentIssues.length} documents couldn't be accessed`}
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.documentHeaderText}
          />
        </View>

        <View style={styles.documentList}>
          {review.documentIssues.map((documentIssue) => (
            <View key={documentIssue.id} style={styles.documentIssueCard}>
              {renderDocumentIssueIcon(documentIssue.documentIssueKind)}
              <View style={styles.documentTextWrap}>
                <CustomText
                  text={documentIssue.title}
                  useTranslationText={false}
                  size="s"
                  weight="normal"
                  style={styles.documentTitle}
                />
                <CustomText
                  text={documentIssue.message}
                  useTranslationText={false}
                  size="xs"
                  weight="normal"
                  style={styles.documentMessage}
                />
              </View>
            </View>
          ))}
        </View>
      </View>
    );

  const renderOverview = () => (
    <>
      {renderGapAnalysis()}
      {renderDocumentIssues()}
    </>
  );

  const renderArchitectContent = () => {
    if (activeTab === "team") {
      return teamManagement ? (
        <TeamManagementPanel
          team={teamManagement}
          currentUserId={currentUserId}
          userOptions={teamUserOptions}
          searchQuery={teamSearchQuery}
          onSearchQueryChange={onTeamSearchQueryChange}
          selectedRole={selectedTeamRole}
          onSelectedRoleChange={onSelectedTeamRoleChange}
          isSaving={isSavingTeamManagement}
          onEditingTeamChange={onEditingTeamChange}
        />
      ) : (
        <View style={styles.card}>
          <CustomText
            text="Team management is not available for this course yet."
            size="s"
            style={styles.documentMessage}
            useTranslationText={false}
          />
        </View>
      );
    }

    if (activeTab === "gapAnalysis") {
      return renderGapAnalysis();
    }

    return renderOverview();
  };

  return (
    <View style={styles.panel}>
      {isArchitect ? (
        <>
          <View
            style={styles.tabBar}
            accessibilityRole="tablist"
            accessibilityLabel="Overview panel tabs"
            role="tablist"
            aria-label="Overview panel tabs">
            <TabButton label="Overview" isActive={activeTab === "overview"} onPress={() => setActiveTab("overview")} />
            <TabButton
              label="Gap analysis"
              isActive={activeTab === "gapAnalysis"}
              onPress={() => setActiveTab("gapAnalysis")}
            />
            <TabButton label="Team management" isActive={activeTab === "team"} onPress={() => setActiveTab("team")} />
          </View>
          {renderArchitectContent()}
        </>
      ) : (
        renderOverview()
      )}
    </View>
  );
}

function TabButton({ label, isActive, onPress }: { label: string; isActive: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={label}
      role="tab"
      aria-selected={isActive}
      aria-label={label}
      style={[styles.tabButton, isActive ? styles.tabButtonActive : styles.tabButtonInactive]}>
      <CustomText
        text={label}
        useTranslationText={false}
        size="s"
        weight="medium"
        style={isActive ? styles.tabTextActive : styles.tabTextInactive}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 24,
  },
  tabBar: {
    flexDirection: "row",
    gap: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    minHeight: 48,
    alignItems: "center",
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
  },
  tabButtonActive: {
    borderBottomColor: colors.brand[600],
  },
  tabButtonInactive: {
    borderBottomColor: "transparent",
  },
  tabTextActive: {
    color: "#3459D4",
  },
  tabTextInactive: {
    color: colors.grey[700],
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 20,
  },
  documentHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  documentHeaderText: {
    color: "#DC6803",
  },
  documentList: {
    gap: 10,
  },
  documentIssueCard: {
    backgroundColor: "#FFF8E8",
    borderWidth: 1,
    borderColor: "#FCE7B0",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  documentTextWrap: {
    flex: 1,
  },
  documentTitle: {
    color: colors.grey[800],
    marginBottom: 2,
  },
  documentMessage: {
    color: colors.grey[500],
  },
});

export default OverviewPanel;
